<template>
  <div class="relative" :class="dropdownClasses">
    <select
      :id="id"
      v-model="selectedOption"
      class="dropdown-input"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :required="required"
      :disabled="disabled"
      :name="name"
    >
      <!-- needs to accept an object or an array -->
      <option
        v-for="(option, index) in options"
        :id="index"
        :key="index"
        :value="option.hasOwnProperty('value') ? option.value : option"
      >
        {{ option.display ? option.display : option }}
      </option>
    </select>
    <label :class="dropdownSelectedOption" :for="id">{{ labelName }}</label>
    <slot />

    <div v-if="helperText" :id="ariaDescribedByName" class="mt-4 flex relative">
      <ErrorIcon v-if="hasError" class="mr-4 mt-4" />
      <p class="dropdown-helper">{{ helperText }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import helperStatus from '../../helpers/status';
import selectionOptions from '../../helpers/selection';
import ErrorIcon from '../../icons/ErrorIcon.vue';

export default Vue.extend({
  name: 'Dropdown',
  components: {
    ErrorIcon,
  },
  props: {
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    options: {
      type: Array as () => selectionOptions[],
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    helperText: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
    },
    required: {
      type: Boolean,
    },
  },
  data(): any {
    return {
      selectedOption: this.value,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      if (!this.helperText) {
        return null;
      }
      return `${this.id}-helper`;
    },
    dropdownSelectedOption(): String {
      if (this.selectedOption) {
        return 'dropdown-label-filled';
      }
      return 'dropdown-label';
    },
    dropdownClasses(): String {
      if (this.hasError) {
        return 'dropdown-error';
      }
      return 'dropdown';
    },
    hasError(): Boolean {
      return this.status === helperStatus.Error;
    },
  },
  watch: {
    // watch for data changes so the parent can be notified
    selectedOption(value: String | Number) {
      if (value) {
        this.$emit('input', value);
      }
    },
    value(v: String | Number) {
      // watching both the prop and the data causes infinite feedback loops,
      // checking the value has actually changed prevents the loop.
      if (v !== this.selectedOption) {
        this.selectedOption = v;
      }
    },
  },
});
</script>
